import { Box, Dialog, DialogContent, LinearProgress, Link, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { WarningAmberRounded } from '@mui/icons-material';
import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { PermissionCode, PlanLimit } from '@/graphql/generated/types.generated';
import { PlanLimitsDocument } from '@/graphql/generated/plan-limits.generated';
import { PlanPermissionPricesDocument } from '@/graphql/generated/plan-permission-prices.generated';
import { monetaryFormat } from '@/utils/monetary-format';
import PlanLimitUpgrade from './PlanLimitUpgrade';

/** Billing route + hash that opens the plans modal straight on the add-ons calculator. */
export const X_CREDITS_ADDON_PATH = '/settings/workspace/billing#addons';

// Backend i18n keys thrown by the schedule-time X gate (PostService.validateXProfilesAccess).
const X_CREDITS_ERROR_KEYS = new Set(['error.x-credits-insufficient', 'error.x-addon-required']);

// Module-level store so the dialog is mounted once (PrivateLayout) and any post action can open it.
let messageKey: string | null = null;
const listeners = new Set<() => void>();
const setMessageKey = (next: string | null) => {
    messageKey = next;
    listeners.forEach((l) => l());
};
const subscribe = (l: () => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
};

/**
 * Error handler for post actions (schedule, publish, retry): an X credits refusal opens the
 * buy-credits dialog, anything else goes to the snackbar as before.
 */
export function usePostActionError() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    return (error: { message: string }) => {
        if (X_CREDITS_ERROR_KEYS.has(error.message)) {
            setMessageKey(error.message);
            return;
        }
        enqueueSnackbar(t(error.message));
    };
}

/** The smallest block of X credits the add-on sells, priced from the live permission prices. */
function useXCreditsBlockOffer() {
    const { data } = useQuery(PlanPermissionPricesDocument);
    const perm = data?.planPermissionPrices.find((p) => p.code === PermissionCode.XCredits);
    const unitPrice = perm?.price?.flat?.monthlyPrice ?? perm?.price?.tier?.[0]?.monthlyPrice;
    if (!perm || !unitPrice) return null;

    const credits = perm.increments || 1;
    return { credits, price: monetaryFormat(unitPrice * credits, perm.price?.currency) };
}

function XCreditsUsage() {
    const { t } = useTranslation();
    const { data } = useQuery(PlanLimitsDocument, {
        variables: { planLimits: [PlanLimit.XCredits] },
        fetchPolicy: 'cache-and-network',
    });
    const usage = data?.planLimits[0];
    const limit = Number(usage?.limit ?? 0);
    // No add-on yet: there is no meter to show, the offer below says it all.
    if (!usage || limit <= 0) return null;

    const used = Math.min(Number(usage.current), limit);
    return (
        <Stack gap={0.5}>
            <LinearProgress
                variant="determinate"
                value={(used / limit) * 100}
                color="error"
                sx={{ height: 6, borderRadius: 3 }}
            />
            <Typography variant="caption" color="text.secondary">
                {t('andie.usage.used-counter', { used, limit })}
            </Typography>
        </Stack>
    );
}

export default function XCreditsDialog() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const current = useSyncExternalStore(subscribe, () => messageKey);
    const offer = useXCreditsBlockOffer();
    const close = () => setMessageKey(null);

    return (
        <Dialog open={current !== null} onClose={close} maxWidth="xs" fullWidth data-id="x-credits-dialog">
            <DialogContent>
                <Box
                    sx={(theme) => ({
                        borderRadius: 2,
                        p: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                    })}
                >
                    <Stack spacing={2}>
                        <Stack spacing={0.75}>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                <WarningAmberRounded sx={{ fontSize: 16, color: 'primary.main' }} />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 600, lineHeight: 1.2 }}
                                >
                                    {t('tool.publishing-activity.profile.error.X_CREDITS_EXHAUSTED.title')}
                                </Typography>
                            </Stack>
                            {current && (
                                <Typography variant="body2" color="text.secondary">
                                    {t(current)}
                                </Typography>
                            )}
                            <XCreditsUsage />
                            {offer && (
                                <Typography
                                    variant="subtitle2"
                                    color="text.primary"
                                    sx={{ fontWeight: 700, lineHeight: 1.4, textWrap: 'balance' }}
                                >
                                    {t('x-credits.dialog.headline', offer)}
                                </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                {t('x-credits.dialog.subtitle')}
                            </Typography>
                        </Stack>
                        <Stack spacing={1}>
                            <PlanLimitUpgrade
                                planLimit={PlanLimit.XCredits}
                                variant="button"
                                ctaLabel={t('x-credits.buy-cta')}
                                path={X_CREDITS_ADDON_PATH}
                                onUpgrade={close}
                                sx={{ py: 1, fontWeight: 600 }}
                            />
                            <Typography variant="caption" color="text.secondary" textAlign="center">
                                {t('andie.usage.change-plan-prompt')}{' '}
                                <Link
                                    component="button"
                                    type="button"
                                    variant="caption"
                                    underline="always"
                                    color="inherit"
                                    sx={{ fontWeight: 600 }}
                                    onClick={() => {
                                        close();
                                        navigate('/settings/workspace/billing#upgrade');
                                    }}
                                >
                                    {t('andie.usage.view-plans')}
                                </Link>
                            </Typography>
                        </Stack>
                    </Stack>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
